import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Layout } from '@/components/Layout';
import { useApi } from "@/hooks/useApi";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Save, Plus, X, Bell } from "lucide-react";
import { toast } from "sonner";

// Checklist Settings Component
const ChecklistSettingsCard = () => {
  const api = useApi();
  const [checklistSettings, setChecklistSettings] = useState({
    notification_times: ["12:00", "15:30"],
    notification_recipients: ["admin", "assigned_nurse"],
    sms_enabled: true,
    email_enabled: true
  });
  const [loading, setLoading] = useState(false);
  const [newTime, setNewTime] = useState("");

  useEffect(() => {
    loadChecklistSettings();
  }, []);

  const loadChecklistSettings = async () => {
    try {
      const data = await api.get("/checklist/settings");
      if (data) {
        setChecklistSettings(data);
      }
    } catch (error) {
      console.error("Failed to load checklist settings:", error);
    }
  };

  const saveChecklistSettings = async () => {
    setLoading(true);
    try {
      await api.put("/checklist/settings", checklistSettings);
      toast.success("Checklist notification settings saved");
    } catch (error) {
      console.error("Failed to save checklist settings:", error);
      toast.error("Failed to save checklist settings");
    } finally {
      setLoading(false);
    }
  };

  const addNotificationTime = () => {
    if (newTime && !checklistSettings.notification_times.includes(newTime)) {
      setChecklistSettings({
        ...checklistSettings,
        notification_times: [...checklistSettings.notification_times, newTime]
      });
      setNewTime("");
    }
  };

  const removeNotificationTime = (time) => {
    setChecklistSettings({
      ...checklistSettings,
      notification_times: checklistSettings.notification_times.filter(t => t !== time)
    });
  };

  const toggleRecipient = (recipient) => {
    const recipients = checklistSettings.notification_recipients;
    if (recipients.includes(recipient)) {
      setChecklistSettings({
        ...checklistSettings,
        notification_recipients: recipients.filter(r => r !== recipient)
      });
    } else {
      setChecklistSettings({
        ...checklistSettings,
        notification_recipients: [...recipients, recipient]
      });
    }
  };

  return (
    <Card className="rounded-2xl border-[#e5e5e5]">
      <CardHeader>
        <CardTitle className="flex items-center gap-2" style={{ color: "#652D90" }}>
          <Bell size={20} />
          Checklist Notifications
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <p className="text-sm text-gray-600">
          Configure automated notifications for incomplete checklists
        </p>

        {/* Notification Times */}
        <div className="space-y-3">
          <Label style={{ color: "#652D90", fontWeight: 600 }}>Notification Times</Label>
          <p className="text-xs text-gray-500">
            Set times when admins will be notified if checklist has pending items
          </p>
          <div className="flex gap-2">
            <Input
              type="time"
              value={newTime}
              onChange={(e) => setNewTime(e.target.value)}
              className="rounded-xl"
            />
            <Button
              onClick={addNotificationTime}
              size="sm"
              variant="outline"
              className="rounded-full"
            >
              <Plus size={16} />
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {checklistSettings.notification_times.map((time) => (
              <div
                key={time}
                className="flex items-center gap-1 bg-purple-100 px-3 py-1 rounded-full"
              >
                <span className="text-sm" style={{ color: "#652D90" }}>{time}</span>
                <button
                  onClick={() => removeNotificationTime(time)}
                  className="hover:text-red-600"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Recipients */}
        <div className="space-y-3">
          <Label style={{ color: "#652D90", fontWeight: 600 }}>Notification Recipients</Label>
          <div className="space-y-2">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={checklistSettings.notification_recipients.includes("admin")}
                onChange={() => toggleRecipient("admin")}
                className="h-4 w-4"
              />
              <span className="text-sm">Clinic Admin</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={checklistSettings.notification_recipients.includes("assigned_nurse")}
                onChange={() => toggleRecipient("assigned_nurse")}
                className="h-4 w-4"
              />
              <span className="text-sm">Assigned Nurse</span>
            </label>
          </div>
        </div>

        {/* Notification Methods */}
        <div className="space-y-3">
          <Label style={{ color: "#652D90", fontWeight: 600 }}>Notification Methods</Label>
          <div className="space-y-2">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={checklistSettings.sms_enabled}
                onChange={(e) => setChecklistSettings({...checklistSettings, sms_enabled: e.target.checked})}
                className="h-4 w-4"
              />
              <span className="text-sm">SMS Notification</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={checklistSettings.email_enabled}
                onChange={(e) => setChecklistSettings({...checklistSettings, email_enabled: e.target.checked})}
                className="h-4 w-4"
              />
              <span className="text-sm">Email Notification</span>
            </label>
          </div>
        </div>

        <Button
          onClick={saveChecklistSettings}
          disabled={loading}
          style={{ backgroundColor: "#652D90" }}
          className="w-full rounded-xl"
        >
          {loading ? "Saving..." : "Save Notification Settings"}
        </Button>
      </CardContent>
    </Card>
  );
};


export const SettingsPage = () => {
  const { user, token } = useAuth();
  const api = useApi(token);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [settings, setSettings] = useState({
    support_email: "",
    support_contact: "",
    support_text: "",
    google_analytics_code: "",
    sender_email: "",
    brevo_api_key: "",
    sms_sender_key: "",
    sms_footer: "",  // Global SMS footer
    website: "",
    logo_url: "",
    social_media_links: []
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const data = await api.get("/settings");
      if (data) {
        setSettings({
          ...data,
          social_media_links: data.social_media_links || []
        });
      }
    } catch (error) {
      console.error("Failed to load settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post("/settings", settings);
      toast.success("Settings saved successfully!");
    } catch (error) {
      console.error("Failed to save settings:", error);
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const addSocialLink = () => {
    setSettings({
      ...settings,
      social_media_links: [
        ...settings.social_media_links,
        { platform: "", url: "" }
      ]
    });
  };

  const updateSocialLink = (index, field, value) => {
    const updated = [...settings.social_media_links];
    updated[index][field] = value;
    setSettings({ ...settings, social_media_links: updated });
  };

  const removeSocialLink = (index) => {
    const updated = settings.social_media_links.filter((_, i) => i !== index);
    setSettings({ ...settings, social_media_links: updated });
  };

  if (user?.role !== "superadmin") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Access denied. Superadmin only.</p>
      </div>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-bold" style={{ color: "#652D90" }}>System Settings</h1>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="rounded-full"
            style={{ backgroundColor: "#652D90" }}
          >
            <Save size={16} className="mr-2" />
            {saving ? "Saving..." : "Save Settings"}
          </Button>
        </div>

        {loading ? (
          <p>Loading settings...</p>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            {/* Support Configuration */}
            <Card className="rounded-2xl border-[#e5e5e5]">
              <CardHeader>
                <CardTitle style={{ color: "#652D90" }}>Support Configuration</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Support Email</Label>
                  <Input
                    type="email"
                    placeholder="support@example.com"
                    value={settings.support_email}
                    onChange={(e) => setSettings({ ...settings, support_email: e.target.value })}
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <Label>Support Contact</Label>
                  <Input
                    placeholder="+1 234 567 8900"
                    value={settings.support_contact}
                    onChange={(e) => setSettings({ ...settings, support_contact: e.target.value })}
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <Label>Support Text (Visible to clinic users)</Label>
                  <Textarea
                    placeholder="Contact support if you encounter any issues..."
                    value={settings.support_text}
                    onChange={(e) => setSettings({ ...settings, support_text: e.target.value })}
                    className="rounded-xl"
                    rows={4}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Email & SMS Configuration */}
            <Card className="rounded-2xl border-[#e5e5e5]">
              <CardHeader>
                <CardTitle style={{ color: "#652D90" }}>Email & SMS Configuration</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Sender Email</Label>
                  <Input
                    type="email"
                    placeholder="noreply@example.com"
                    value={settings.sender_email}
                    onChange={(e) => setSettings({ ...settings, sender_email: e.target.value })}
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <Label>Brevo API Key</Label>
                  <Input
                    type="password"
                    placeholder="xkeysib-..."
                    value={settings.brevo_api_key}
                    onChange={(e) => setSettings({ ...settings, brevo_api_key: e.target.value })}
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <Label>SMS Sender Key (Coming Soon)</Label>
                  <Input
                    type="password"
                    placeholder="SMS API Key"
                    value={settings.sms_sender_key}
                    onChange={(e) => setSettings({ ...settings, sms_sender_key: e.target.value })}
                    className="rounded-xl"
                    disabled
                  />
                  <p className="text-xs mt-1" style={{ color: "#666" }}>SMS feature will be implemented soon</p>
                </div>
                <div>
                  <Label>Global SMS Footer</Label>
                  <Textarea
                    placeholder="Footer text appended to all SMS (e.g., clinic name, contact info)"
                    value={settings.sms_footer}
                    onChange={(e) => setSettings({ ...settings, sms_footer: e.target.value })}
                    className="rounded-xl"
                    rows={2}
                  />
                  <p className="text-xs mt-1" style={{ color: "#666" }}>
                    This footer will be automatically added to all SMS sent from the system. 
                    Character count includes this footer.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Branding & Analytics */}
            <Card className="rounded-2xl border-[#e5e5e5]">
              <CardHeader>
                <CardTitle style={{ color: "#652D90" }}>Branding & Analytics</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Website URL</Label>
                  <Input
                    placeholder="https://example.com"
                    value={settings.website}
                    onChange={(e) => setSettings({ ...settings, website: e.target.value })}
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <Label>Logo URL</Label>
                  <Input
                    placeholder="https://example.com/logo.png"
                    value={settings.logo_url}
                    onChange={(e) => setSettings({ ...settings, logo_url: e.target.value })}
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <Label>Google Analytics Code</Label>
                  <Input
                    placeholder="G-XXXXXXXXXX"
                    value={settings.google_analytics_code}
                    onChange={(e) => setSettings({ ...settings, google_analytics_code: e.target.value })}
                    className="rounded-xl"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Checklist Notification Settings */}
            {(user?.role === 'clinic_admin' || user?.role === 'superadmin') && (
              <ChecklistSettingsCard />
            )}

            {/* Social Media Links */}
            <Card className="rounded-2xl border-[#e5e5e5]">
              <CardHeader>
                <CardTitle className="flex justify-between items-center">
                  <span style={{ color: "#652D90" }}>Social Media Links</span>
                  <Button
                    onClick={addSocialLink}
                    size="sm"
                    variant="outline"
                    className="rounded-full"
                  >
                    <Plus size={16} className="mr-1" />
                    Add Link
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {settings.social_media_links.length === 0 ? (
                  <p className="text-sm" style={{ color: "#666" }}>No social media links added yet</p>
                ) : (
                  settings.social_media_links.map((link, idx) => (
                    <div key={idx} className="flex gap-2">
                      <Select
                        value={link.platform}
                        onValueChange={(value) => updateSocialLink(idx, "platform", value)}
                      >
                        <SelectTrigger className="rounded-xl w-[200px]">
                          <SelectValue placeholder="Select platform" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Facebook">Facebook</SelectItem>
                          <SelectItem value="Twitter">Twitter</SelectItem>
                          <SelectItem value="Instagram">Instagram</SelectItem>
                          <SelectItem value="TikTok">TikTok</SelectItem>
                          <SelectItem value="YouTube">YouTube</SelectItem>
                          <SelectItem value="Threads">Threads</SelectItem>
                          <SelectItem value="LinkedIn">LinkedIn</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input
                        placeholder="URL"
                        value={link.url}
                        onChange={(e) => updateSocialLink(idx, "url", e.target.value)}
                        className="rounded-xl"
                      />
                      <Button
                        onClick={() => removeSocialLink(idx)}
                        variant="outline"
                        size="sm"
                        className="rounded-full"
                      >
                        <X size={16} />
                      </Button>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </Layout>
  );
};
